import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Timer, Play, Pause, Square, RotateCcw, Plus, Bell, Settings } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import toast from 'react-hot-toast';

interface TimerState {
  id: string;
  name: string;
  totalSeconds: number;
  remainingSeconds: number;
  isRunning: boolean;
  isFinished: boolean;
  startTime?: Date;
}

interface TimerPreset {
  name: string;
  seconds: number;
}

export const CountdownTimerTool: React.FC = () => {
  const { t } = useTranslation();
  const [timers, setTimers] = useState<TimerState[]>([]);
  const [newTimerName, setNewTimerName] = useState('');
  const [newTimerHours, setNewTimerHours] = useState(0);
  const [newTimerMinutes, setNewTimerMinutes] = useState(5);
  const [newTimerSeconds, setNewTimerSeconds] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [notificationEnabled, setNotificationEnabled] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const presets: TimerPreset[] = [
    { name: 'Pomodoro', seconds: 25 * 60 },
    { name: 'Short Break', seconds: 5 * 60 },
    { name: 'Long Break', seconds: 15 * 60 },
    { name: 'Tea Timer', seconds: 3 * 60 },
    { name: 'Egg Timer', seconds: 7 * 60 },
    { name: 'Workout', seconds: 30 * 60 }
  ];

  // Request notification permission
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then(permission => {
        setNotificationEnabled(permission === 'granted');
      });
    } else {
      setNotificationEnabled(Notification.permission === 'granted');
    }
  }, []);

  // Create audio element for sound notifications
  useEffect(() => {
    // Create a simple beep sound using Web Audio API
    const createBeepSound = () => {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    };

    audioRef.current = {
      play: createBeepSound
    } as any;
  }, []);

  // Timer tick effect
  useEffect(() => {
    if (timers.some(timer => timer.isRunning)) {
      intervalRef.current = setInterval(() => {
        setTimers(prevTimers => 
          prevTimers.map(timer => {
            if (!timer.isRunning || timer.remainingSeconds <= 0) {
              return timer;
            }

            const newRemainingSeconds = timer.remainingSeconds - 1;
            
            if (newRemainingSeconds <= 0) {
              // Timer finished
              handleTimerFinished(timer);
              return {
                ...timer,
                remainingSeconds: 0,
                isRunning: false,
                isFinished: true
              };
            }

            return {
              ...timer,
              remainingSeconds: newRemainingSeconds
            };
          })
        );
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [timers]);

  const handleTimerFinished = (timer: TimerState) => {
    // Play sound notification
    if (soundEnabled && audioRef.current) {
      try {
        audioRef.current.play();
      } catch (error) {
        console.log('Could not play sound:', error);
      }
    }

    // Show browser notification
    if (notificationEnabled && 'Notification' in window) {
      new Notification(t('common.timerFinished', { name: timer.name }), {
        body: t('common.timerFinishedBody'),
        icon: '/favicon.ico'
      });
    }

    // Show toast notification
    toast.success(t('common.timerFinished', { name: timer.name }), {
      duration: 5000,
      icon: '⏰'
    });
  };

  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const addTimer = (name?: string, totalSeconds?: number) => {
    const timerName = name || newTimerName || `Timer ${timers.length + 1}`;
    const seconds = totalSeconds || (newTimerHours * 3600 + newTimerMinutes * 60 + newTimerSeconds);
    
    if (seconds <= 0) {
      toast.error('Please set a valid time');
      return;
    }

    const newTimer: TimerState = {
      id: Date.now().toString(),
      name: timerName,
      totalSeconds: seconds,
      remainingSeconds: seconds,
      isRunning: false,
      isFinished: false
    };

    setTimers([...timers, newTimer]);
    
    // Reset form
    setNewTimerName('');
    setNewTimerHours(0);
    setNewTimerMinutes(5);
    setNewTimerSeconds(0);
  };

  const startTimer = (id: string) => {
    setTimers(timers.map(timer => 
      timer.id === id 
        ? { ...timer, isRunning: true, isFinished: false, startTime: new Date() }
        : timer
    ));
  };

  const pauseTimer = (id: string) => {
    setTimers(timers.map(timer => 
      timer.id === id 
        ? { ...timer, isRunning: false }
        : timer
    ));
  };

  const stopTimer = (id: string) => {
    setTimers(timers.map(timer => 
      timer.id === id 
        ? { 
            ...timer, 
            isRunning: false, 
            isFinished: false,
            remainingSeconds: timer.totalSeconds 
          }
        : timer
    ));
  };

  const resetTimer = (id: string) => {
    setTimers(timers.map(timer => 
      timer.id === id 
        ? { 
            ...timer, 
            isRunning: false, 
            isFinished: false,
            remainingSeconds: timer.totalSeconds 
          }
        : timer
    ));
  };

  const removeTimer = (id: string) => {
    setTimers(timers.filter(timer => timer.id !== id));
  };

  const getProgressPercentage = (timer: TimerState): number => {
    return ((timer.totalSeconds - timer.remainingSeconds) / timer.totalSeconds) * 100;
  };

  const getTimerColor = (timer: TimerState): string => {
    if (timer.isFinished) return 'text-green-600 dark:text-green-400';
    if (timer.remainingSeconds <= 60) return 'text-red-600 dark:text-red-400';
    if (timer.remainingSeconds <= 300) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-blue-600 dark:text-blue-400';
  };

  const getProgressColor = (timer: TimerState): string => {
    if (timer.isFinished) return 'bg-green-500';
    if (timer.remainingSeconds <= 60) return 'bg-red-500';
    if (timer.remainingSeconds <= 300) return 'bg-yellow-500';
    return 'bg-blue-500';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Timer className="w-5 h-5" />
            <span>Countdown Timer</span>
          </CardTitle>
        </CardHeader>
      </Card>

      {/* Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Settings className="w-5 h-5" />
            <span>Settings</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={soundEnabled}
                onChange={(e) => setSoundEnabled(e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <Bell className="w-4 h-4" />
              <span className="text-sm">Sound notifications</span>
            </label>
            
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={notificationEnabled}
                onChange={(e) => setNotificationEnabled(e.target.checked)}
                disabled={!('Notification' in window) || Notification.permission === 'denied'}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm">Browser notifications</span>
            </label>
          </div>
        </CardContent>
      </Card>

      {/* Add Timer */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Plus className="w-5 h-5" />
            <span>Add Timer</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Input
              label="Timer Name"
              value={newTimerName}
              onChange={(e) => setNewTimerName(e.target.value)}
              placeholder="My Timer"
            />
            
            <div className="grid grid-cols-3 gap-4">
              <Input
                label="Hours"
                type="number"
                min="0"
                max="23"
                value={newTimerHours}
                onChange={(e) => setNewTimerHours(parseInt(e.target.value) || 0)}
              />
              <Input
                label="Minutes"
                type="number"
                min="0"
                max="59"
                value={newTimerMinutes}
                onChange={(e) => setNewTimerMinutes(parseInt(e.target.value) || 0)}
              />
              <Input
                label="Seconds"
                type="number"
                min="0"
                max="59"
                value={newTimerSeconds}
                onChange={(e) => setNewTimerSeconds(parseInt(e.target.value) || 0)}
              />
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => addTimer()}
                variant="primary"
              >
                Add Timer
              </Button>
              
              {presets.map((preset) => (
                <Button
                  key={preset.name}
                  onClick={() => addTimer(preset.name, preset.seconds)}
                  variant="outline"
                  size="sm"
                >
                  {preset.name}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Active Timers */}
      {timers.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Active Timers</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {timers.map((timer) => (
                <div
                  key={timer.id}
                  className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800"
                >
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-medium text-gray-900 dark:text-gray-100">
                      {timer.name}
                    </h3>
                    <Button
                      onClick={() => removeTimer(timer.id)}
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                    >
                      ×
                    </Button>
                  </div>

                  <div className="text-center mb-4">
                    <div className={`text-4xl font-mono font-bold ${getTimerColor(timer)}`}>
                      {formatTime(timer.remainingSeconds)}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {timer.isFinished ? 'Finished!' : 
                       timer.isRunning ? 'Running' : 'Paused'}
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="w-full bg-gray-200 rounded-full h-2 mb-4 dark:bg-gray-700">
                    <div
                      className={`h-2 rounded-full transition-all duration-1000 ${getProgressColor(timer)}`}
                      style={{ width: `${getProgressPercentage(timer)}%` }}
                    />
                  </div>

                  {/* Controls */}
                  <div className="flex justify-center space-x-2">
                    {!timer.isRunning ? (
                      <Button
                        onClick={() => startTimer(timer.id)}
                        disabled={timer.remainingSeconds <= 0}
                        variant="primary"
                        size="sm"
                      >
                        <Play className="w-4 h-4 mr-1" />
                        Start
                      </Button>
                    ) : (
                      <Button
                        onClick={() => pauseTimer(timer.id)}
                        variant="outline"
                        size="sm"
                      >
                        <Pause className="w-4 h-4 mr-1" />
                        Pause
                      </Button>
                    )}
                    
                    <Button
                      onClick={() => stopTimer(timer.id)}
                      variant="outline"
                      size="sm"
                    >
                      <Square className="w-4 h-4 mr-1" />
                      Stop
                    </Button>
                    
                    <Button
                      onClick={() => resetTimer(timer.id)}
                      variant="outline"
                      size="sm"
                    >
                      <RotateCcw className="w-4 h-4 mr-1" />
                      Reset
                    </Button>
                  </div>

                  {/* Timer Info */}
                  <div className="mt-3 text-xs text-gray-500 dark:text-gray-400 text-center">
                    Total: {formatTime(timer.totalSeconds)}
                    {timer.startTime && timer.isRunning && (
                      <span className="ml-2">
                        Started: {timer.startTime.toLocaleTimeString()}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Usage Guide */}
      <Card>
        <CardHeader>
          <CardTitle>Usage Guide</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Features:</h4>
              <ul className="space-y-1 text-gray-600 dark:text-gray-400">
                <li>• Multiple simultaneous timers</li>
                <li>• Customizable timer names</li>
                <li>• Sound and browser notifications</li>
                <li>• Progress visualization</li>
                <li>• Quick preset timers</li>
                <li>• Pause, stop, and reset controls</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">Presets:</h4>
              <ul className="space-y-1 text-gray-600 dark:text-gray-400">
                <li>• <strong>Pomodoro:</strong> 25 minutes for focused work</li>
                <li>• <strong>Short Break:</strong> 5 minutes for quick breaks</li>
                <li>• <strong>Long Break:</strong> 15 minutes for longer breaks</li>
                <li>• <strong>Tea Timer:</strong> 3 minutes for brewing tea</li>
                <li>• <strong>Egg Timer:</strong> 7 minutes for soft-boiled eggs</li>
                <li>• <strong>Workout:</strong> 30 minutes for exercise sessions</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
